import React, { useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Trophy, ExternalLink, Info, X, ChevronDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { jsonrepair } from 'jsonrepair';
import { useOptionalAuth } from '../../../context/AuthContext';
import { RadiusProvider } from '../radiusScoring/RadiusContext';
import RadiusText from '../radiusScoring/RadiusText';
import RadiusLedger from '../radiusScoring/RadiusLedger';
import type { RadiusBlock } from '../radiusScoring/radiusTypes';

/* ═══════════════════════════════════════════════════
   Types — matches GET /api/concept-test-vitamin/{id}
   ═══════════════════════════════════════════════════ */

interface KScore {
  mean?: number;
  pct?: number;
  std?: number;
  benchmark?: number;  // Norm value from API (when enable_norms is true)
  delta?: number;      // Pre-calculated mean - benchmark
  verdict?: 'pass' | 'warn' | 'fail';  // Pre-calculated verdict
  distribution?: Record<string, number>;
  n?: number;
}

interface ConfidenceMetric {
  score: number;           // Confidence score (0-100)
  margin_of_error: number; // e.g., 0.17
  ci_low: number;          // Confidence interval low bound
  ci_high: number;         // Confidence interval high bound
}

interface Confidence {
  k1?: ConfidenceMetric;
  k2?: ConfidenceMetric;
  k3?: ConfidenceMetric;
  k4?: ConfidenceMetric;
  overall_reliability?: number;
  n?: number;
}

interface ReferenceProduct {
  name: string;
  k1?: number;
  k2?: number;
  k3?: number;
  k4?: number;
}

interface Norms {
  category_mean?: Record<string, number>;
  reference_products?: ReferenceProduct[];
  concept_vs_norm?: Record<string, string>;
  concept_rank?: number;
  concept_percentile?: number;
  confidence?: Confidence;
}

interface SegmentData {
  name: string;
  description: string;
  n_agents: number;
  metrics: Record<string, KScore>;
  top_strength: { metric: string; label: string };
  primary_friction: { metric: string; label: string };
}

interface FrictionItem {
  rank: number;
  name: string;
  pct: number;
  count: number;
}

interface DriverItem {
  rank: number;
  name: string;
  pct: number;
  count: number;
  bar_width?: number;
}

interface InsightCard {
  headline: string;
  body: string;
}

interface VitaminReport {
  test_id: string;
  concept: Record<string, any>;
  population: string;
  n_agents: number;
  timestamp: string;
  overall_composite_pct: number;
  k_scores: Record<string, KScore>;
  segments: Record<string, SegmentData>;
  friction_drivers: {
    frictions: FrictionItem[];
    drivers: DriverItem[];
    quotes?: string[];
  };
  agent_results?: Record<string, any>;
  generated?: {
    verdict_text: string;
    segment_insights: Record<string, string>;
    friction_driver_descriptions: Record<string, string>;
    overall_insights: {
      cards: InsightCard[];
      strongest_signal?: string;
      biggest_risk?: string;
    };
  };
  _config?: Record<string, any>;
  norms?: Norms;
  /** Present only when the run was started with enable_radius_scoring. */
  radius?: RadiusBlock;
}

/* ═══════════════════════════════════════════════════
   Multi-Concept Types
   ═══════════════════════════════════════════════════ */

interface MultiConceptWinner {
  product_headline: string;
  brand_name: string;
  overall_mean: number;
  why_winner_won: string;
  winner_verbatim_summary: string;
  product_image_url?: string;
}

interface ConceptRanking {
  rank: number;
  product_headline: string;
  brand_name: string;
  price_point?: string;
  overall_mean: number;
  k1_mean?: number;
  k2_mean?: number;
  k3_mean?: number;
  k4_mean?: number;
  top_2_box?: Record<string, number>;
  report_html?: string;
  // Differentiation fields
  concept_str?: string;     // Full concept text
  concept_summary?: string; // 1-liner summary
  diff_value?: string;      // Differentiation value (e.g., "90 gummies")
}

interface MultiConceptComparison {
  friction_drivers: string;
  overall_insights: string;
}

interface MultiConceptConceptData {
  index: number;
  product_headline: string;
  brand_name: string;
  price_point?: string;
  product_image_url?: string;
  overall_mean: number;
  n_agents?: number;
  k_scores: Record<string, KScore>;
  top_2_box?: Record<string, number>;
  friction_drivers: {
    frictions: FrictionItem[];
    drivers: DriverItem[];
    quotes?: string[];
  };
  segments?: Record<string, SegmentData>;
  report_html?: string;
  report_json?: string;
  // Differentiation fields
  concept_str?: string;     // Full concept text
  concept_summary?: string; // 1-liner summary
  diff_value?: string;      // Differentiation value (e.g., "90 gummies")
  // API does return generated data for each concept in multi-concept tests
  generated?: {
    verdict_text: string;
    segment_insights: Record<string, string>;
    friction_driver_descriptions: Record<string, string>;
    overall_insights: {
      cards: InsightCard[];
      strongest_signal: string;
      biggest_risk: string;
    };
  };
  norms?: Norms;
}

interface MultiConceptResponse {
  test_id: string;
  mode: 'multi';
  n_concepts: number;
  n_agents?: number;
  population: string;
  winner: MultiConceptWinner;
  ranking: ConceptRanking[];
  comparison: MultiConceptComparison;
  concepts: MultiConceptConceptData[];
  diff_variable?: string; // What's being compared (e.g., "Gummy count")
  norms?: Norms;
}

type ParsedReport =
  | { mode: 'single'; data: VitaminReport }
  | { mode: 'multi'; data: MultiConceptResponse };

/* ═══════════════════════════════════════════════════
   Brand palette (BluePill)
   ═══════════════════════════════════════════════════ */

const BRAND = {
  accent: '#07E5D1',
  accentLight: '#E8FCFA',
  accentHover: '#06D4C2',
  dark: '#012e2a',
};

// Colors for differentiating concepts with same name
const CONCEPT_COLORS = ['#07E5D1', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

// Scaling thresholds for studies with many concepts
const MAX_TABS_BEFORE_DROPDOWN = 6;
const MAX_COMPARISON_CARDS_COLLAPSED = 8;
const RANKED_LIST_SCROLL_HINT_MIN = 6;
// diff_value is meant to be a short differentiator label (e.g. "90 gummies");
// some payloads mis-feed the full pipe-joined concept string into it —
// detect those structurally, with a generous length cap as the last resort
const MAX_DIFF_VALUE_LABEL_CHARS = 80;

// Helper function to calculate Top 2 Box percentage from distribution
const calculateTop2Box = (score: { distribution?: Record<string, number> } | undefined): number => {
  if (!score?.distribution) return 0;
  const dist = score.distribution;
  const top2 = (dist['5'] || 0) + (dist['4'] || 0);
  const total = Object.values(dist).reduce((sum, v) => sum + v, 0);
  return total > 0 ? Math.round((top2 / total) * 100) : 0;
};

const K_LABELS: Record<string, string> = {
  k1: 'Purchase Intent',
  k2: 'Value Perception',
  k3: 'New & Different',
  k4: 'Believability',
};

const K_ORDER = ['k1', 'k2', 'k3', 'k4'];

// Hardcoded norms for specific test IDs (custom report overrides)
// Each report can be accessed via /analysis/{id} or /simulation/public/{shareToken}
const HARDCODED_NORMS: Record<string, Record<string, number>> = {
  // Olipop Raspberry Sherbet report - /analysis/2774 or /simulation/public/zwrdakI-...
  'zwrdakI-KH_Qgys6McPv6LKe5m-TNjjVyM7unZr0zX0': {
    k1: 3.65,
    k2: 3.70,
    k3: 3.60,
    k4: 3.95,
  },
  '2774': {
    k1: 3.65,
    k2: 3.70,
    k3: 3.60,
    k4: 3.95,
  },
  // Grass-fed hot honey ranch report - /analysis/2775 or /simulation/public/nHfgEPNv1...
  'nHfgEPNv1Ry0xGK5ba0aORE-y4ln8_lKFkP9SNHgbuA': {
    k1: 3.60,
    k2: 3.50,
    k3: 3.50,
    k4: 4.05,
  },
  '2775': {
    k1: 3.60,
    k2: 3.50,
    k3: 3.50,
    k4: 4.05,
  },
};


// Helper to get hardcoded norm for a specific test and metric (legacy fallback)
const getHardcodedNorm = (testId: string | undefined, key: string): number | null => {
  if (!testId) return null;
  return HARDCODED_NORMS[testId]?.[key] ?? null;
};

// Helper to get norm: prioritize API benchmark over hardcoded
const getNorm = (
  kScore: KScore | undefined,
  testId: string | undefined,
  key: string
): number | null => {
  // First priority: API-returned benchmark
  if (kScore?.benchmark !== undefined) {
    return kScore.benchmark;
  }
  // Second priority: hardcoded norms for specific test IDs
  return getHardcodedNorm(testId, key);
};


const VERDICT_STYLE: Record<string, { bg: string; border: string; textClass: string; label: string; dotClass: string }> = {
  pass: { bg: 'bg-teal-50', border: 'border-teal-200', textClass: 'text-teal-700', label: 'above category norm', dotClass: 'bg-teal-500' },
  warn: { bg: 'bg-amber-50', border: 'border-amber-200', textClass: 'text-amber-700', label: 'at category norm — watch', dotClass: 'bg-amber-500' },
  fail: { bg: 'bg-red-50', border: 'border-red-200', textClass: 'text-red-700', label: 'below category norm', dotClass: 'bg-red-500' },
};

/* ═══════════════════════════════════════════════════
   Parse helper
   ═══════════════════════════════════════════════════ */

function parseReport(raw: any): ParsedReport | null {
  if (!raw) return null;
  try {
    const data = typeof raw === 'string' ? JSON.parse(jsonrepair(raw)) : raw;

    // Check for multi-concept response
    if (data.mode === 'multi' || (data.results && data.results.mode === 'multi')) {
      const multiData = data.results || data;
      return { mode: 'multi', data: multiData as MultiConceptResponse };
    }

    // Single concept response
    if (data.k_scores && data.overall_composite_pct !== undefined) {
      return { mode: 'single', data: data as VitaminReport };
    }
    if (data.results && data.results.k_scores) {
      return { mode: 'single', data: data.results as VitaminReport };
    }

    // Fallback - try to parse as single
    if (data.k_scores || data.overall_composite_pct !== undefined) {
      return { mode: 'single', data: data as VitaminReport };
    }

    return null;
  } catch {
    return null;
  }
}

/* ═══════════════════════════════════════════════════
   Section Label
   ═══════════════════════════════════════════════════ */

const SectionLabel: React.FC<{ num: string; label: string }> = ({ num, label }) => (
  <div className="flex items-center gap-3 mb-7">
    <span className="text-xs font-bold tracking-wider font-mono tabular-nums" style={{ color: BRAND.accent }}>{num}</span>
    <span className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: BRAND.dark }}>{label}</span>
    <div className="flex-1 h-px" style={{ backgroundColor: `${BRAND.accent}30` }} />
  </div>
);

/* ═══════════════════════════════════════════════════
   Helper: Get concept string by index from contentData
   ═══════════════════════════════════════════════════ */

const getConceptStringByIndex = (contentData: any, index: number): string => {
  // Multi-concept mode: contentData.concepts[index].concept_str
  if (contentData?.concepts && Array.isArray(contentData.concepts)) {
    return contentData.concepts[index]?.concept_str || '';
  }
  // Single concept mode: contentData.concept_str
  return contentData?.concept_str || '';
};

/* ═══════════════════════════════════════════════════
   Concept Info Button - small (i) button
   ═══════════════════════════════════════════════════ */

const ConceptInfoButton: React.FC<{ onClick: (e: React.MouseEvent) => void; className?: string }> = ({ onClick, className = '' }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick(e); }}
    className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-400 hover:text-[#07E5D1] hover:bg-[#E8FCFA] transition-colors cursor-pointer ml-1.5 ${className}`}
    title="View full concept details"
  >
    <Info className="w-3.5 h-3.5" />
  </button>
);

/* ═══════════════════════════════════════════════════
   Concept Info Modal - shows full concept string
   ═══════════════════════════════════════════════════ */

interface ConceptInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandName: string;
  conceptString: string;
}

// Helper to clean markdown artifacts from text
const cleanMarkdown = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/^\*\*\s*/, '')  // Remove leading **
    .replace(/\s*\*\*$/, '')  // Remove trailing **
    .replace(/\*\*/g, '')     // Remove any remaining **
    .trim();
};

// Helper to parse concept string into structured fields
const parseConceptString = (conceptStr: string): Record<string, string> => {
  const fields: Record<string, string> = {};
  if (!conceptStr) return fields;

  // Strategy 1: Line-by-line parsing with **Label:** Value format
  const lines = conceptStr.split('\n');
  let currentKey = '';
  let currentValue = '';

  for (const line of lines) {
    // Match **Field Name:** or **Field Name**: patterns
    const match = line.match(/^\*\*([^*:]+)\*\*:\s*(.*)/);
    if (match) {
      // Save previous field if exists
      if (currentKey && currentValue.trim()) {
        fields[currentKey] = cleanMarkdown(currentValue.trim());
      }
      currentKey = match[1].trim();
      currentValue = match[2] || '';
    } else if (currentKey && line.trim()) {
      // Continue previous field value (multi-line content)
      currentValue += '\n' + line;
    }
  }
  // Save last field
  if (currentKey && currentValue.trim()) {
    fields[currentKey] = cleanMarkdown(currentValue.trim());
  }

  if (Object.keys(fields).length > 0) return fields;

  // Strategy 2: Split by **Field Name:** pattern (fallback)
  const parts = conceptStr.split(/\*\*([^*]+)\*\*:\s*/);
  if (parts.length > 2) {
    for (let i = 1; i < parts.length - 1; i += 2) {
      const key = parts[i].trim();
      const value = cleanMarkdown(parts[i + 1]?.trim() || '');
      if (key && value) {
        fields[key] = value;
      }
    }
    if (Object.keys(fields).length > 0) return fields;
  }

  // Strategy 3: Plain text with Label: Value format (no bold markers)
  const plainLines = conceptStr.split('\n');
  for (const line of plainLines) {
    const colonIdx = line.indexOf(':');
    if (colonIdx > 0 && colonIdx < 30) {
      const key = line.substring(0, colonIdx).replace(/\*\*/g, '').trim();
      const value = cleanMarkdown(line.substring(colonIdx + 1).trim());
      if (key && value && key.split(' ').length <= 4) {
        fields[key] = value;
      }
    }
  }

  return fields;
};

// Field name normalization - map variations to canonical names
const normalizeFieldName = (name: string): string => {
  const normalized = name.toLowerCase().replace(/[_-]/g, ' ').trim();
  const mapping: Record<string, string> = {
    'product headline': 'Product Headline',
    'headline': 'Product Headline',
    'brand name': 'Brand Name',
    'brand': 'Brand Name',
    'one line description': 'Description',
    'description': 'Description',
    'short description': 'Description',
    'detailed description': 'Detailed Description',
    'long description': 'Detailed Description',
    'reasons to believe': 'Reasons to Believe',
    'rtb': 'Reasons to Believe',
    'benefits': 'Reasons to Believe',
    'price & pack': 'Price & Pack',
    'price and pack': 'Price & Pack',
    'price pack': 'Price & Pack',
    'price': 'Price & Pack',
    'product image url': 'Product Image URL',
    'image url': 'Product Image URL',
    'image': 'Product Image URL',
  };
  return mapping[normalized] || name;
};

// Get field value with fallbacks for different naming conventions
const getField = (fields: Record<string, string>, ...keys: string[]): string => {
  for (const key of keys) {
    if (fields[key]) return fields[key];
    // Also try normalized lookup
    for (const [fieldKey, fieldValue] of Object.entries(fields)) {
      if (normalizeFieldName(fieldKey) === key) return fieldValue;
    }
  }
  return '';
};

// Rankings are Purchase-Intent (k1) based; fall back to overall_mean when absent
const getConceptK1Mean = (concept: { k_scores?: Record<string, KScore>; overall_mean: number }): number =>
  concept.k_scores?.k1?.mean ?? concept.overall_mean;

// A diff_value is only displayable when it is a genuine short label,
// not a mis-fed full concept string (pipe-joined or markdown-formatted)
const getDisplayableDiffValue = (diffValue?: string): string | null => {
  if (!diffValue) return null;
  if (diffValue.includes('**') || diffValue.includes(' | ') || diffValue.includes('\n')) return null;
  const cleaned = cleanMarkdown(diffValue);
  if (!cleaned || cleaned.length > MAX_DIFF_VALUE_LABEL_CHARS) return null;
  return cleaned;
};

// concept_summary sometimes carries the full raw-markdown concept string
// instead of a 1-liner; extract a short readable summary either way
const getDisplayableSummary = (summary?: string): string | null => {
  if (!summary) return null;
  if (summary.includes('**')) {
    const fields = parseConceptString(summary);
    const short =
      getField(fields, 'Description', 'One Line Description', 'Short Description') ||
      getField(fields, 'Detailed Description', 'Long Description');
    if (short) return short.replace(/\s*\n\s*/g, ' ');
    // Looks like a full concept string (headline/brand/price fields) with no
    // description — don't dump the flattened string into a one-liner slot.
    // Summaries that merely contain bold markup still fall through below.
    const conceptStrMarkers = ['Product Headline', 'Brand Name', 'Price & Pack'];
    if (conceptStrMarkers.some(marker => getField(fields, marker))) return null;
  }
  const cleaned = cleanMarkdown(summary).replace(/\s*\n\s*/g, ' ');
  return cleaned || null;
};

const ConceptInfoModal: React.FC<ConceptInfoModalProps> = ({ isOpen, onClose, brandName, conceptString }) => {
  if (!isOpen) return null;

  const fields = parseConceptString(conceptString);
  const fieldCount = Object.keys(fields).length;

  // Extract known fields with fallbacks
  const productHeadline = getField(fields, 'Product Headline', 'Headline');
  const parsedBrandName = getField(fields, 'Brand Name', 'Brand') || brandName;
  const description = getField(fields, 'Description', 'One Line Description', 'Short Description');
  const detailedDescription = getField(fields, 'Detailed Description', 'Long Description');
  const reasonsToBelieve = getField(fields, 'Reasons to Believe', 'RTB', 'Benefits');
  const pricePack = getField(fields, 'Price & Pack', 'Price and Pack', 'Price');
  // Parse price & pack into separate fields
  const parsePackInfo = (packStr: string) => {
    const price = packStr.match(/\$[\d.,]+/)?.[0] || '';
    const servings = packStr.match(/(\d+)\s*servings?/i)?.[0] || '';
    const packSize = packStr.match(/(\d+)\s*(gummies|capsules|tablets|count)/i)?.[0] || '';
    return { price, servings, packSize, hasDetails: !!(price || servings || packSize) };
  };
  const packInfo = parsePackInfo(pricePack);

  // Get remaining fields
  const knownFields = new Set([
    'Product Headline', 'Headline', 'Brand Name', 'Brand',
    'Description', 'One Line Description', 'Short Description',
    'Detailed Description', 'Long Description',
    'Reasons to Believe', 'RTB', 'Benefits',
    'Price & Pack', 'Price and Pack', 'Price',
    'Product Image URL', 'Image URL', 'Image'
  ]);
  const additionalFields = Object.entries(fields).filter(([key]) => {
    return !knownFields.has(key) && !knownFields.has(normalizeFieldName(key));
  });

  // Parse RTB into bullet points
  const rtbLines = reasonsToBelieve
    ? reasonsToBelieve.split('\n')
        .map(line => cleanMarkdown(line.replace(/^[-•]\s*/, '')))
        .filter(line => line && line !== '**')
    : [];

  // Build all detail rows for display
  const detailRows: { label: string; value: string; isPrice?: boolean }[] = [];

  if (parsedBrandName && parsedBrandName !== brandName) {
    detailRows.push({ label: 'Brand', value: parsedBrandName });
  }
  if (productHeadline) {
    detailRows.push({ label: 'Headline', value: productHeadline });
  }
  if (description) {
    detailRows.push({ label: 'Description', value: description });
  }
  if (pricePack) {
    detailRows.push({ label: 'Price & Pack', value: pricePack, isPrice: true });
  }
  if (detailedDescription) {
    detailRows.push({ label: 'Details', value: detailedDescription });
  }

  // Add additional fields
  additionalFields.forEach(([key, value]) => {
    detailRows.push({ label: key, value });
  });

  // Modal styles matching the elegant dark theme
  const styles = {
    surface: '#0a2725',
    border: 'rgba(7, 229, 209, 0.1)',
    textMuted: 'rgba(230, 244, 241, 0.45)',
    textDim: 'rgba(230, 244, 241, 0.3)',
    tealSoft: 'rgba(7, 229, 209, 0.08)',
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
      style={{
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{
          background: styles.surface,
          borderRadius: '20px',
          border: `1px solid ${styles.border}`,
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5), 0 1px 0 rgba(255, 255, 255, 0.03) inset',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ══════════════════════════════════════════════════
            HEADER
            ══════════════════════════════════════════════════ */}
        <div className="px-7 pt-7 pb-6 relative overflow-hidden">
          {/* Decorative arcs */}
          <div
            className="absolute pointer-events-none"
            style={{
              width: 500, height: 500, borderRadius: '50%',
              border: `1px solid rgba(7, 229, 209, 0.04)`,
              top: '50%', right: '-30%',
              transform: 'translateY(-50%)',
            }}
          />
          <div
            className="absolute pointer-events-none"
            style={{
              width: 350, height: 350, borderRadius: '50%',
              border: `1px solid rgba(7, 229, 209, 0.06)`,
              top: '50%', right: '-20%',
              transform: 'translateY(-50%)',
            }}
          />

          {/* Header top row */}
          <div className="flex items-center justify-between mb-6 relative z-10">
            {/* Brand pill */}
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.08em]"
              style={{ backgroundColor: BRAND.accent, color: '#052621' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#052621]" />
              {parsedBrandName}
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-all"
              style={{ color: styles.textMuted }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = styles.tealSoft;
                e.currentTarget.style.color = BRAND.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = styles.textMuted;
              }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Title area */}
          <div className="relative z-10">
            <div
              className="text-[11px] font-semibold uppercase tracking-[0.14em] mb-2"
              style={{ color: styles.textDim }}
            >
              Full Concept Details
            </div>
            <h2 className="text-2xl font-semibold text-white leading-tight tracking-tight">
              {productHeadline ? (
                <>
                  {productHeadline.split(' ').slice(0, -2).join(' ')}{' '}
                  <span className="italic font-light" style={{ color: styles.textMuted }}>
                    {productHeadline.split(' ').slice(-2).join(' ')}
                  </span>
                </>
              ) : (
                parsedBrandName
              )}
            </h2>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════
            BODY - Detail rows
            ══════════════════════════════════════════════════ */}
        <div className="flex-1 overflow-y-auto px-7 pb-5">
          {detailRows.map((row, idx) => (
            <div
              key={idx}
              className="grid py-4"
              style={{
                gridTemplateColumns: '130px 1fr',
                gap: '20px',
                alignItems: 'start',
                borderBottom: idx < detailRows.length - 1 ? `1px solid rgba(7, 229, 209, 0.06)` : 'none',
              }}
            >
              <span
                className="text-[11px] font-medium uppercase tracking-[0.08em] pt-0.5"
                style={{ color: styles.textDim }}
              >
                {row.label}
              </span>
              {row.isPrice && packInfo.hasDetails ? (
                <span className="flex items-baseline gap-2.5 flex-wrap">
                  <span className="font-semibold text-lg" style={{ color: BRAND.accent }}>
                    {packInfo.price}
                  </span>
                  {(packInfo.packSize || packInfo.servings) && (
                    <>
                      <span style={{ color: styles.textDim }}>·</span>
                      <span className="text-sm" style={{ color: styles.textMuted }}>
                        {[packInfo.packSize, packInfo.servings].filter(Boolean).join(', ')}
                      </span>
                    </>
                  )}
                </span>
              ) : (
                <span className="text-[15px] text-white leading-relaxed">
                  {row.value}
                </span>
              )}
            </div>
          ))}

          {/* Reasons to Believe - special display */}
          {rtbLines.length > 0 && (
            <div
              className="py-4"
              style={{ borderBottom: additionalFields.length > 0 ? `1px solid rgba(7, 229, 209, 0.06)` : 'none' }}
            >
              <span
                className="text-[11px] font-medium uppercase tracking-[0.08em] block mb-3"
                style={{ color: styles.textDim }}
              >
                Reasons to Believe
              </span>
              <div className="space-y-2">
                {rtbLines.map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span
                      className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                      style={{ backgroundColor: BRAND.accent }}
                    />
                    <span className="text-[15px] text-white leading-relaxed">{line}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fallback for unparsed content */}
          {fieldCount === 0 && conceptString && (
            <div className="py-4">
              {conceptString.split('\n').filter(line => line.trim()).map((line, i) => {
                const boldMatch = line.match(/^\*\*([^*:]+)\*\*:\s*(.*)/);
                const colonMatch = !boldMatch && line.match(/^([A-Za-z][^:]{0,29}):\s*(.*)/);

                if (boldMatch || colonMatch) {
                  const colMatch = colonMatch as RegExpMatchArray;
                  const label = boldMatch ? boldMatch[1].trim() : cleanMarkdown(colMatch[1]);
                  const value = cleanMarkdown(boldMatch ? (boldMatch[2] || '') : (colMatch[2] || ''));
                  return (
                    <div
                      key={i}
                      className="grid py-3"
                      style={{
                        gridTemplateColumns: '130px 1fr',
                        gap: '20px',
                        borderBottom: `1px solid rgba(7, 229, 209, 0.06)`,
                      }}
                    >
                      <span className="text-[11px] font-medium uppercase tracking-[0.08em]" style={{ color: styles.textDim }}>
                        {label}
                      </span>
                      <span className="text-[15px] text-white leading-relaxed">{value}</span>
                    </div>
                  );
                } else {
                  const cleaned = cleanMarkdown(line);
                  if (!cleaned) return null;
                  return (
                    <p key={i} className="text-[15px] text-white/80 leading-relaxed py-1">{cleaned}</p>
                  );
                }
              })}
            </div>
          )}

          {/* Empty state */}
          {!conceptString && detailRows.length === 0 && (
            <div className="py-12 text-center">
              <FileText className="w-8 h-8 mx-auto mb-3" style={{ color: styles.textDim }} />
              <p className="text-sm" style={{ color: styles.textMuted }}>No concept details available.</p>
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════════════
            FOOTER
            ══════════════════════════════════════════════════ */}
        <div
          className="px-7 py-4 flex items-center justify-between"
          style={{ borderTop: `1px solid ${styles.border}` }}
        >
          <span
            className="text-[10px] font-medium uppercase tracking-[0.1em]"
            style={{ color: styles.textDim }}
          >
            BluePill AI · Concept Details
          </span>
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-semibold rounded-lg transition-all hover:shadow-lg"
            style={{
              backgroundColor: BRAND.accent,
              color: '#052621',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#5eead4';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(7, 229, 209, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = BRAND.accent;
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════ */

interface Props {
  simulationResponse: any;
  contentData: any;
  isPreparingExport?: boolean;
  shareToken?: string;
  isPrintMode?: boolean;
}

const VitaminConceptTestResults: React.FC<Props> = ({ simulationResponse, contentData, shareToken, isPrintMode = false }) => {
  const parsed = useMemo(() => parseReport(simulationResponse), [simulationResponse]);
  // Source attribution is an internal QA instrument: admins only, and only when the
  // run was actually scored. Everyone else sees the report exactly as before.
  // Optional auth — this view also renders on paths with no AuthProvider (print/export),
  // where the overlay must simply stay off rather than break the report.
  const isAdmin = useOptionalAuth()?.isAdmin ?? false;

  if (!parsed) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="w-8 h-8 mx-auto mb-3 text-gray-400" />
        <p>Unable to parse concept test results.</p>
      </div>
    );
  }

  // Multi-concept mode
  if (parsed.mode === 'multi') {
    return <MultiConceptResultsView data={parsed.data} contentData={contentData} isPrintMode={isPrintMode} />;
  }

  // Single-concept mode
  const report = parsed.data;
  const displayName = (contentData?.name as string) || report?.concept?.brand_name || 'Concept Report';

  if (report.overall_composite_pct === undefined) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="w-8 h-8 mx-auto mb-3 text-gray-400" />
        <p>Unable to parse concept test results.</p>
      </div>
    );
  }

  return (
    <RadiusProvider block={report.radius} enabled={isAdmin} isPrintMode={isPrintMode}>
      <SingleConceptResultsView report={report} displayName={displayName} contentData={contentData} shareToken={shareToken} isPrintMode={isPrintMode} />
    </RadiusProvider>
  );
};

/* ═══════════════════════════════════════════════════
   SINGLE CONCEPT RESULTS VIEW
   ═══════════════════════════════════════════════════ */

interface SingleConceptResultsViewProps {
  report: VitaminReport;
  displayName: string;
  contentData?: any;
  shareToken?: string;
  isPrintMode?: boolean;
}

const SingleConceptResultsView: React.FC<SingleConceptResultsViewProps> = ({ report, displayName, contentData, shareToken, isPrintMode = false }) => {
  const [showConceptInfo, setShowConceptInfo] = useState(false);
  const conceptString = getConceptStringByIndex(contentData, 0);

  const kKeys = K_ORDER.filter(k => report.k_scores[k]);
  const segEntries = Object.entries(report.segments || {})
    .map(([id, seg]) => ({ id, ...seg }))
    .sort((a, b) => (b.metrics?.k1?.mean ?? 0) - (a.metrics?.k1?.mean ?? 0));

  return (
    <div className={`space-y-10 ${isPrintMode ? 'print-mode overflow-visible h-auto' : ''}`}>
      <CoverSection
        report={report}
        displayName={displayName}
        conceptString={conceptString}
        onShowConceptInfo={() => setShowConceptInfo(true)}
      />
      <ExecSummary report={report} kKeys={kKeys} testId={shareToken || report.test_id} />
      {segEntries.length > 0 && <SegmentBreakdown report={report} kKeys={kKeys} segEntries={segEntries} />}
      <FrictionAndDrivers report={report} />
      {report.generated?.overall_insights?.cards && <OverallInsights report={report} />}

      {/* Concept Info Modal */}
      <ConceptInfoModal
        isOpen={showConceptInfo}
        onClose={() => setShowConceptInfo(false)}
        brandName={report.concept?.brand_name || displayName}
        conceptString={conceptString}
      />
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MULTI-CONCEPT RESULTS VIEW
   ═══════════════════════════════════════════════════ */

interface MultiConceptResultsViewProps {
  data: MultiConceptResponse;
  contentData: any;
  isPrintMode?: boolean;
}

const MultiConceptResultsView: React.FC<MultiConceptResultsViewProps> = ({ data, contentData, isPrintMode = false }) => {
  const individualReportsSectionRef = useRef<HTMLDivElement>(null);

  const displayName = (contentData?.name as string) || 'Multi-Concept Comparison';

  // State for concept info modal
  const [conceptModal, setConceptModal] = useState<{ brandName: string; conceptString: string } | null>(null);

  // Handler to show concept info - uses array index to find concept, then concept.index for original order
  const handleShowConceptInfo = (arrayIndex: number) => {
    const concept = data.concepts[arrayIndex];
    // Use concept.index (original submission order) to get the correct concept string from contentData
    const originalIndex = concept?.index ?? arrayIndex;
    // Prefer concept_str from API response (ranking or concepts array), fall back to contentData
    const ranking = data.ranking[arrayIndex];
    const conceptStr = ranking?.concept_str || concept?.concept_str || getConceptStringByIndex(contentData, originalIndex);
    setConceptModal({
      brandName: concept?.brand_name || `Concept ${arrayIndex + 1}`,
      conceptString: conceptStr,
    });
  };

  // Find the winner index by matching brand_name, product_headline, AND overall_mean to handle duplicates
  const winnerIndex = useMemo(() => {
    const idx = data.concepts.findIndex(
      c => c.brand_name === data.winner.brand_name &&
           c.product_headline === data.winner.product_headline &&
           Math.abs(c.overall_mean - data.winner.overall_mean) < 0.001
    );
    // Fallback: if not found by all three, try finding by rank 1 in ranking
    if (idx === -1 && data.ranking.length > 0) {
      const winnerRanking = data.ranking.find(r => r.rank === 1);
      if (winnerRanking) {
        return data.concepts.findIndex(
          c => c.brand_name === winnerRanking.brand_name &&
               c.product_headline === winnerRanking.product_headline &&
               Math.abs(c.overall_mean - winnerRanking.overall_mean) < 0.001
        );
      }
    }
    return idx !== -1 ? idx : 0;
  }, [data.concepts, data.winner, data.ranking]);

  // Initialize activeReportIndex to winner so that winner tab is selected by default
  const [activeReportIndex, setActiveReportIndex] = useState(() => {
    const idx = data.concepts.findIndex(
      c => c.brand_name === data.winner.brand_name &&
           c.product_headline === data.winner.product_headline &&
           Math.abs(c.overall_mean - data.winner.overall_mean) < 0.001
    );
    if (idx === -1 && data.ranking.length > 0) {
      const winnerRanking = data.ranking.find(r => r.rank === 1);
      if (winnerRanking) {
        return data.concepts.findIndex(
          c => c.brand_name === winnerRanking.brand_name &&
               c.product_headline === winnerRanking.product_headline &&
               Math.abs(c.overall_mean - winnerRanking.overall_mean) < 0.001
        );
      }
    }
    return idx !== -1 ? idx : 0;
  });

  // Handler that sets active tab and scrolls to the individual reports section
  const handleViewDetails = (index: number) => {
    setActiveReportIndex(index);
    // Scroll to the individual reports section after a small delay to allow state update
    setTimeout(() => {
      individualReportsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className={`space-y-10 ${isPrintMode ? 'print-mode overflow-visible h-auto' : ''}`}>
      {/* Cover Section - Multi */}
      <MultiConceptCoverSection data={data} displayName={displayName} />

      {/* Section 01: Winner & Rankings */}
      <WinnerHighlightSection
        winner={data.winner}
        ranking={data.ranking}
        concepts={data.concepts}
        winnerIndex={winnerIndex}
        onViewDetails={handleViewDetails}
        onShowConceptInfo={handleShowConceptInfo}
        diffVariable={data.diff_variable}
      />

      {/* Section 02: Friction & Drivers Comparison */}
      <ComparisonFrictionDrivers
        comparison={data.comparison}
        concepts={data.concepts}
        winnerIndex={winnerIndex}
        onShowConceptInfo={handleShowConceptInfo}
        diffVariable={data.diff_variable}
        isPrintMode={isPrintMode}
      />

      {/* Section 03: Overall Comparison Insights */}
      <ComparisonOverallInsights comparison={data.comparison} />

      {/* Section 04: Individual Reports */}
      <IndividualReportsSection
        ref={individualReportsSectionRef}
        concepts={data.concepts}
        activeIndex={activeReportIndex}
        onTabChange={setActiveReportIndex}
        winnerIndex={winnerIndex}
        onShowConceptInfo={handleShowConceptInfo}
        diffVariable={data.diff_variable}
      />

      {/* Concept Info Modal */}
      <ConceptInfoModal
        isOpen={conceptModal !== null}
        onClose={() => setConceptModal(null)}
        brandName={conceptModal?.brandName || ''}
        conceptString={conceptModal?.conceptString || ''}
      />
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MULTI-CONCEPT COVER SECTION
   ═══════════════════════════════════════════════════ */

const MultiConceptCoverSection: React.FC<{ data: MultiConceptResponse; displayName: string }> = ({ data, displayName }) => {
  // Use top-level n_agents if available, otherwise sum from concepts. No hardcoded fallback:
  // if neither is present the run genuinely reported no agent count, so show 0 (an evident bug
  // signal) rather than the old plausible-but-wrong 126.
  const totalAgents = data.n_agents || data.concepts.reduce((sum, c) => sum + (c.n_agents || 0), 0) || 0;
  const stats = [
    { value: `N=${totalAgents}`, label: 'Consumer twins' },
    { value: String(data.n_concepts), label: 'Concepts tested' },
    { value: 'Comparison', label: 'Study type' },
    { value: String(Object.keys(data.concepts[0]?.k_scores || {}).length || 4), label: 'Core metrics' },
    // Add reliability if confidence data is available
    ...(data.norms?.confidence?.overall_reliability !== undefined
      ? [{ value: `${data.norms.confidence.overall_reliability}%`, label: 'Reliability' }]
      : []),
  ];

  // Find the winning concept to get its product image
  const winningConcept = data.concepts.find(
    c => c.product_headline === data.winner.product_headline
  );
  const winnerImageUrl = winningConcept?.product_image_url || data.winner.product_image_url;
  const hasWinnerImage = winnerImageUrl && winnerImageUrl.trim() !== '';

  return (
    <div className="rounded-lg shadow-sm overflow-hidden relative" style={{ backgroundColor: BRAND.dark }}>
      {/* Decorative arcs */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: 600, height: 600, borderRadius: '50%',
          border: `1px solid ${BRAND.accent}12`,
          top: -200, right: -150,
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          width: 400, height: 400, borderRadius: '50%',
          border: `1px solid ${BRAND.accent}08`,
          top: -80, right: -50,
        }}
      />

      <div className="px-5 pt-6 pb-5 md:px-8 md:pt-8 md:pb-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-8 md:mb-10 md:flex-row md:items-start md:justify-between md:gap-6">
          <div className="flex items-center gap-3">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.09em] px-3 py-1.5 rounded-full"
              style={{ backgroundColor: BRAND.accent, color: BRAND.dark }}
            >
              BluePill AI
            </span>
            <span className="text-xs text-white/40 tracking-wide">× Multi-Concept Test</span>
          </div>
          <div className="text-[11px] text-white/35 text-right leading-relaxed">
            {data.test_id && <>Study ID: BP-MCT-{data.test_id.slice(0, 6)}<br /></>}
            Version: 1.0 — Final
          </div>
        </div>

        {/* Title block with optional winner image */}
        <div className={`mb-12 ${hasWinnerImage ? 'flex gap-8' : ''}`}>
          <div className={hasWinnerImage ? 'flex-1 min-w-0' : 'min-w-0'}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-4">CPG Multi-Concept Comparison</div>
            <h2 className="text-2xl md:text-3xl font-semibold text-white leading-tight mb-3">
              Concept Comparison<br />
              <em className="font-normal text-white/40">& Winner Analysis</em>
            </h2>
            <p className="text-sm text-white/50 leading-relaxed max-w-2xl">
              <strong className="text-white/80">{displayName}</strong>
              {' — '}Comparing {data.n_concepts} concepts to identify the strongest performer
            </p>
          </div>

          {/* Winner Product Image */}
          {hasWinnerImage && (
            <div className="flex-shrink-0 flex flex-col items-center">
              <div
                className="w-40 h-40 rounded-lg overflow-hidden flex items-center justify-center"
                style={{
                  border: `2px solid ${BRAND.accent}40`,
                  backgroundColor: 'rgba(255,255,255,0.05)'
                }}
              >
                <img
                  src={winnerImageUrl}
                  alt={data.winner.product_headline}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                <Trophy className="h-3 w-3" style={{ color: BRAND.accent }} />
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-medium">Winner</span>
              </div>
            </div>
          )}
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap gap-x-8 gap-y-4 md:gap-12 pt-6" style={{ borderTop: `1px solid ${BRAND.accent}18` }}>
          {stats.map(s => (
            <div key={s.label}>
              <div className="text-4xl font-bold leading-none mb-1 tabular-nums" style={{ color: BRAND.accent }}>{s.value}</div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-white/35">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer note */}
      <div className="px-8 py-3 flex items-center justify-between gap-4 relative z-10" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-[10px] text-white/25 leading-relaxed">
          Produced using BluePill AI&rsquo;s synthetic consumer twin methodology. Multi-concept comparison with winner analysis.
        </p>
        <span className="text-[9px] uppercase tracking-wider text-white/20 font-semibold whitespace-nowrap">Confidential</span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 01: Winner & Rankings
   ═══════════════════════════════════════════════════ */

interface WinnerHighlightSectionProps {
  winner: MultiConceptWinner;
  ranking: ConceptRanking[];
  concepts: MultiConceptConceptData[];
  winnerIndex: number;
  onViewDetails: (index: number) => void;
  onShowConceptInfo: (index: number) => void;
  diffVariable?: string; // What's being compared (e.g., "Gummy count")
}

const WinnerHighlightSection: React.FC<WinnerHighlightSectionProps> = ({
  winner,
  ranking,
  concepts,
  winnerIndex,
  onViewDetails,
  onShowConceptInfo,
  diffVariable,
}) => {
  // Use winnerIndex to get the winning concept for k1 (Purchase Intent) score
  const winnerConcept = concepts[winnerIndex];
  const winnerK1Mean = winnerConcept?.k_scores?.k1?.mean ?? winner.overall_mean;

  // Get the ranking entry for the winner to access diff_value
  const winnerRanking = ranking.find(r => r.rank === 1);
  const winnerSummary = getDisplayableSummary(winnerRanking?.concept_summary || winnerConcept?.concept_summary);
  const winnerDiffValue = getDisplayableDiffValue(winnerRanking?.diff_value || winnerConcept?.diff_value);

  return (
    <div>
      <SectionLabel num="01" label="Winner & Rankings" />

      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          The winning <span className="italic font-normal" style={{ color: BRAND.accent }}>concept.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Based on Purchase Intent score. Rankings show relative performance of each concept tested.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 items-start">
        {/* Winner Hero Card */}
        <div className="rounded-lg p-8" style={{ backgroundColor: BRAND.dark }}>
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-6 h-6" style={{ color: BRAND.accent }} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.09em] text-white/50">
              Winning Concept
            </span>
          </div>
          <h3 className="text-2xl font-bold text-white mb-2 flex items-center">
            {winner.brand_name}
            <ConceptInfoButton
              onClick={() => onShowConceptInfo(winnerIndex)}
              className="text-white/40 hover:text-[#07E5D1] hover:bg-white/10"
            />
          </h3>
          <p className="text-sm text-white/60 mb-1">{winner.product_headline}</p>
          {/* Concept summary - 1-liner */}
          {winnerSummary && (
            <p className="text-xs text-white/45 mb-4 line-clamp-2">{winnerSummary}</p>
          )}
          {/* Differentiation pill */}
          {diffVariable && winnerDiffValue && (
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
              style={{ backgroundColor: `${BRAND.accent}15` }}
            >
              <span className="text-xs text-white/50">{diffVariable}:</span>
              <span className="text-xs font-semibold" style={{ color: BRAND.accent }}>
                {winnerDiffValue}
              </span>
            </div>
          )}
          <div className="text-6xl font-bold leading-none mb-1" style={{ color: BRAND.accent }}>
            {winnerK1Mean.toFixed(1)}
            <span className="text-lg text-white/40 ml-2 font-normal">/ 5.0</span>
          </div>
          <div className="text-xs text-white/40">purchase intent</div>
        </div>

        {/* Ranked List */}
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
            All Concepts Ranked
            {ranking.length > RANKED_LIST_SCROLL_HINT_MIN && (
              <span className="ml-2 normal-case tracking-normal font-normal text-gray-400 print:hidden">
                ({ranking.length} concepts — scroll)
              </span>
            )}
          </div>
          <div className="space-y-3 max-h-[640px] overflow-y-auto pr-1">
          {ranking.map((concept, idx) => {
            const isWinner = concept.rank === 1;
            // Use the ranking's own k1_mean directly - each ranking entry has its own scores
            const k1Mean = concept.k1_mean ?? concept.overall_mean;
            // Find concept index in concepts array for navigation (use overall_mean for unique matching)
            const conceptArrayIdx = concepts.findIndex(
              c => Math.abs(c.overall_mean - concept.overall_mean) < 0.001 &&
                   c.brand_name === concept.brand_name
            );
            const summary = getDisplayableSummary(concept.concept_summary);
            const diffValue = getDisplayableDiffValue(concept.diff_value);
            return (
              <div
                key={idx}
                className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                  isWinner
                    ? 'border-teal-200 bg-teal-50/50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    isWinner ? 'text-white' : 'text-gray-600 bg-gray-100'
                  }`}
                  style={isWinner ? { backgroundColor: BRAND.accent } : {}}
                >
                  {concept.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 text-sm">{concept.brand_name}</div>
                  <div className="text-xs text-gray-500">{concept.product_headline}</div>
                  {/* Concept summary if available */}
                  {summary && (
                    <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">{summary}</div>
                  )}
                  {/* Differentiation value */}
                  {diffVariable && diffValue && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-[10px] text-gray-400">{diffVariable}:</span>
                      <span className="text-xs font-medium" style={{ color: BRAND.accent }}>{diffValue}</span>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold" style={{ color: isWinner ? BRAND.dark : '#374151' }}>
                    {k1Mean.toFixed(1)}
                  </div>
                  <div className="text-[10px] text-gray-400">purchase intent</div>
                </div>
                <button
                  onClick={() => onViewDetails(conceptArrayIdx !== -1 ? conceptArrayIdx : idx)}
                  className="p-2 text-gray-400 hover:text-teal-600 transition-colors"
                  title="View full report"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            );
          })}
          </div>
        </div>
      </div>

      {/* Why Winner Won */}
      {winner.why_winner_won && (
        <div className="mt-6 p-5 rounded-lg" style={{ backgroundColor: BRAND.accentLight, border: `1px solid ${BRAND.accent}30` }}>
          <h4 className="text-sm font-semibold mb-2" style={{ color: BRAND.dark }}>
            Why This Concept Won
          </h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            {winner.why_winner_won}
          </p>
        </div>
      )}

      {/* Verbatim Summary */}
      {winner.winner_verbatim_summary && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">
            Consumer Verbatims
          </h4>
          <p className="text-sm text-gray-700 italic leading-relaxed">
            &ldquo;{winner.winner_verbatim_summary}&rdquo;
          </p>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 02: Comparison Friction & Drivers
   ═══════════════════════════════════════════════════ */

interface ComparisonFrictionDriversProps {
  comparison: MultiConceptComparison;
  concepts: MultiConceptConceptData[];
  winnerIndex: number;
  onShowConceptInfo: (index: number) => void;
  diffVariable?: string; // What's being compared
  isPrintMode?: boolean;
}

const ComparisonFrictionDrivers: React.FC<ComparisonFrictionDriversProps> = ({
  comparison,
  concepts,
  winnerIndex,
  onShowConceptInfo,
  diffVariable,
  isPrintMode = false,
}) => {
  // Detect duplicate brand names to show color indicators
  const brandNameCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    concepts.forEach(c => {
      const name = c.brand_name || 'Concept';
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [concepts]);

  const hasDuplicates = Object.values(brandNameCounts).some(count => count > 1);

  // Determine grid columns based on number of concepts
  const gridColsClass = concepts.length === 2
    ? 'grid-cols-1 md:grid-cols-2'
    : concepts.length === 3
    ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
    : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';

  // With many concepts, collapse to the winner + top performers and let the
  // user expand. Print/PDF export must always include every concept — the
  // print CSS can only un-hide DOM that exists, not un-collapse React state.
  const [showAllConcepts, setShowAllConcepts] = useState(false);
  const isCollapsible = !isPrintMode && concepts.length > MAX_COMPARISON_CARDS_COLLAPSED;
  const visibleConcepts = useMemo(() => {
    const indexed = concepts.map((concept, idx) => ({ concept, idx }));
    if (!isCollapsible || showAllConcepts) return indexed;
    const winnerEntry = indexed[winnerIndex] ? [indexed[winnerIndex]] : [];
    const others = indexed
      .filter(({ idx }) => idx !== winnerIndex)
      .sort((a, b) => getConceptK1Mean(b.concept) - getConceptK1Mean(a.concept))
      .slice(0, MAX_COMPARISON_CARDS_COLLAPSED - winnerEntry.length);
    return [...winnerEntry, ...others];
  }, [concepts, winnerIndex, isCollapsible, showAllConcepts]);

  return (
    <div>
      <SectionLabel num="02" label="Friction & Driver Comparison" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          What drove and blocked <span className="italic font-normal" style={{ color: BRAND.accent }}>each concept.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Primary friction categories and purchase drivers for each concept tested.
        </p>
      </div>

      <div className={`grid ${gridColsClass} gap-4`}>
        {visibleConcepts.map(({ concept, idx }) => {
          const brandName = concept.brand_name || `Concept ${idx + 1}`;
          const isWinner = idx === winnerIndex;
          const frictions = concept.friction_drivers?.frictions?.slice(0, 3) || [];
          const drivers = concept.friction_drivers?.drivers?.slice(0, 3) || [];
          const showColorIndicator = hasDuplicates && brandNameCounts[brandName] > 1;
          const conceptColor = CONCEPT_COLORS[idx % CONCEPT_COLORS.length];
          const diffValue = getDisplayableDiffValue(concept.diff_value);

          return (
            <div
              key={concept.index ?? idx}
              className={`rounded-lg p-5 ${
                isWinner
                  ? 'border-2'
                  : 'border border-gray-200 bg-white'
              }`}
              style={isWinner ? { borderColor: BRAND.accent, backgroundColor: BRAND.accentLight + '30' } : {}}
            >
              {/* Concept Header */}
              <div className="flex items-center gap-2 mb-2">
                {showColorIndicator && (
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: conceptColor }}
                    title={`Concept ${idx + 1}`}
                  />
                )}
                {isWinner && <Trophy className="w-4 h-4" style={{ color: BRAND.accent }} />}
                <span className="text-sm font-semibold truncate" style={{ color: isWinner ? BRAND.dark : '#374151' }}>
                  {brandName}
                </span>
                <ConceptInfoButton onClick={() => onShowConceptInfo(idx)} />
                {isWinner && <span className="text-[10px] text-teal-600 flex-shrink-0">(Winner)</span>}
              </div>
              {/* Product headline - differentiates concepts sharing a brand name */}
              {concept.product_headline && concept.product_headline !== brandName && (
                <div className="text-[11px] text-gray-500 mb-2 line-clamp-2">
                  {concept.product_headline}
                </div>
              )}
              {/* Differentiation value */}
              {diffVariable && diffValue && (
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="text-[10px] text-gray-400">{diffVariable}:</span>
                  <span className="text-xs font-medium" style={{ color: isWinner ? BRAND.accent : '#6B7280' }}>
                    {diffValue}
                  </span>
                </div>
              )}

              {/* Frictions */}
              <div className="mb-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-red-600 mb-2">
                  Top Frictions
                </div>
                {frictions.length > 0 ? (
                  <div className="space-y-1.5">
                    {frictions.map((f, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-2 text-sm">
                        <span className="text-red-500 font-medium min-w-[32px]">{f.pct}%</span>
                        <span className="text-gray-700 text-xs leading-tight">{f.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No friction data</p>
                )}
              </div>

              {/* Drivers */}
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: isWinner ? BRAND.dark : '#6B7280' }}>
                  Top Drivers
                </div>
                {drivers.length > 0 ? (
                  <div className="space-y-1.5">
                    {drivers.map((d, dIdx) => (
                      <div key={dIdx} className="flex items-center gap-2 text-sm">
                        <span className="font-medium min-w-[32px]" style={{ color: isWinner ? BRAND.accent : '#6B7280' }}>{d.pct}%</span>
                        <span className="text-gray-700 text-xs leading-tight">{d.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No driver data</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Expand / collapse for many-concept studies */}
      {isCollapsible && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => setShowAllConcepts(prev => !prev)}
            className="px-4 py-2 text-sm font-medium text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors"
          >
            {showAllConcepts
              ? 'Show top concepts only'
              : `Show all ${concepts.length} concepts`}
          </button>
        </div>
      )}

      {/* Comparison Narrative */}
      {comparison.friction_drivers && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">
            Comparison Insight
          </h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            {comparison.friction_drivers}
          </p>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 03: Overall Comparison Insights
   ═══════════════════════════════════════════════════ */

const ComparisonOverallInsights: React.FC<{ comparison: MultiConceptComparison }> = ({ comparison }) => {
  if (!comparison.overall_insights) return null;

  return (
    <div>
      <SectionLabel num="03" label="Overall Insights" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          Cross-concept <span className="italic font-normal" style={{ color: BRAND.accent }}>patterns.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Key insights from comparing all concepts in this study.
        </p>
      </div>

      <div className="rounded-lg p-6" style={{ backgroundColor: BRAND.accentLight, border: `1px solid ${BRAND.accent}35` }}>
        <p className="text-[15px] text-gray-800 leading-relaxed">
          {comparison.overall_insights}
        </p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 04: Individual Reports
   ═══════════════════════════════════════════════════ */

interface ConceptDropdownSelectorProps {
  concepts: MultiConceptConceptData[];
  activeIndex: number;
  onTabChange: (index: number) => void;
  winnerIndex: number;
  onShowConceptInfo: (index: number) => void;
}

// Compact selector for studies with many concepts: prev/next stepper +
// searchable dropdown sorted by purchase-intent score
const ConceptDropdownSelector: React.FC<ConceptDropdownSelectorProps> = ({
  concepts,
  activeIndex,
  onTabChange,
  winnerIndex,
  onShowConceptInfo,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const activeConcept = concepts[activeIndex];
  const activeName = activeConcept?.brand_name || `Concept ${activeIndex + 1}`;

  // Same duplicate-brand disambiguation the tab strip uses
  const brandNameCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    concepts.forEach(c => {
      const name = c.brand_name || 'Concept';
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [concepts]);
  const hasDuplicates = Object.values(brandNameCounts).some(count => count > 1);

  const sortedEntries = useMemo(
    () =>
      concepts
        .map((concept, idx) => ({ concept, idx }))
        .sort((a, b) => getConceptK1Mean(b.concept) - getConceptK1Mean(a.concept)),
    [concepts]
  );

  const filteredEntries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedEntries;
    return sortedEntries.filter(
      ({ concept }) =>
        (concept.brand_name || '').toLowerCase().includes(q) ||
        (concept.product_headline || '').toLowerCase().includes(q)
    );
  }, [sortedEntries, query]);

  const closeDropdown = () => {
    setIsOpen(false);
    setQuery('');
  };

  const selectConcept = (idx: number) => {
    onTabChange(idx);
    closeDropdown();
  };

  const goPrev = () => onTabChange((activeIndex - 1 + concepts.length) % concepts.length);
  const goNext = () => onTabChange((activeIndex + 1) % concepts.length);

  return (
    <div className="relative mb-6 print:hidden">
      <div className={`flex items-stretch gap-2 ${isOpen ? 'relative z-40' : ''}`}>
        <button
          onClick={goPrev}
          className="px-3 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-teal-600 hover:border-teal-200 transition-colors"
          title="Previous concept"
          aria-label="Previous concept"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div
          onClick={() => (isOpen ? closeDropdown() : setIsOpen(true))}
          onKeyDown={e => {
            // Only act when the bar itself is focused — keystrokes from the
            // nested search input / info button must keep their own behavior
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(prev => !prev);
              if (isOpen) setQuery('');
            }
            if (e.key === 'Escape') closeDropdown();
          }}
          tabIndex={0}
          className="flex-1 min-w-0 flex items-center gap-3 px-4 py-2.5 rounded-lg border border-gray-200 bg-white hover:border-teal-300 transition-colors cursor-pointer"
          role="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {activeIndex === winnerIndex && (
            <Trophy className="w-4 h-4 flex-shrink-0" style={{ color: BRAND.accent }} />
          )}
          {hasDuplicates && brandNameCounts[activeName] > 1 && (
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: CONCEPT_COLORS[activeIndex % CONCEPT_COLORS.length] }}
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center text-sm font-semibold text-gray-900">
              <span className="truncate">{activeName}</span>
              <span onClick={e => e.stopPropagation()} className="flex-shrink-0">
                <ConceptInfoButton onClick={() => onShowConceptInfo(activeIndex)} />
              </span>
            </div>
            {activeConcept?.product_headline && activeConcept.product_headline !== activeName && (
              <div className="text-xs text-gray-500 truncate">{activeConcept.product_headline}</div>
            )}
          </div>
          <span className="text-xs text-gray-400 flex-shrink-0">
            {activeIndex + 1} / {concepts.length}
          </span>
          {/* Always-visible search - focusing or typing opens the concept menu */}
          <div className="hidden sm:flex items-center gap-2 pl-3 ml-1 border-l border-gray-200 w-56 flex-shrink-0">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              value={query}
              onFocus={() => setIsOpen(true)}
              onClick={e => {
                e.stopPropagation();
                setIsOpen(true);
              }}
              onChange={e => {
                setQuery(e.target.value);
                setIsOpen(true);
              }}
              onKeyDown={e => {
                if (e.key === 'Escape') closeDropdown();
              }}
              placeholder={`Search ${concepts.length} concepts...`}
              className="w-full text-sm outline-none bg-transparent placeholder:text-gray-400"
            />
          </div>
          <ChevronDown
            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>

        <button
          onClick={goNext}
          className="px-3 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-teal-600 hover:border-teal-200 transition-colors"
          title="Next concept"
          aria-label="Next concept"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={closeDropdown} aria-hidden="true" />
          <div className="absolute left-0 right-0 top-full mt-2 z-40 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            <div className="max-h-80 overflow-y-auto" role="listbox">
              {filteredEntries.length === 0 ? (
                <div className="px-4 py-6 text-sm text-gray-400 text-center">No concepts match</div>
              ) : (
                filteredEntries.map(({ concept, idx }, position) => {
                  const name = concept.brand_name || `Concept ${idx + 1}`;
                  const isActive = idx === activeIndex;
                  const k1Mean = getConceptK1Mean(concept);
                  const diffValue = getDisplayableDiffValue(concept.diff_value);
                  return (
                    <button
                      key={idx}
                      role="option"
                      aria-selected={isActive}
                      onClick={() => selectConcept(idx)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                        isActive ? 'bg-teal-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="w-6 text-xs text-gray-400 text-right flex-shrink-0">
                        {query.trim() ? '' : `#${position + 1}`}
                      </span>
                      {idx === winnerIndex && (
                        <Trophy className="w-3.5 h-3.5 flex-shrink-0" style={{ color: BRAND.accent }} />
                      )}
                      {hasDuplicates && brandNameCounts[name] > 1 && (
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: CONCEPT_COLORS[idx % CONCEPT_COLORS.length] }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${isActive ? 'font-semibold text-teal-700' : 'text-gray-800'}`}>
                          {name}
                          {diffValue && <span className="ml-2 font-normal text-gray-400">{diffValue}</span>}
                        </div>
                        {concept.product_headline && concept.product_headline !== name && (
                          <div className="text-xs text-gray-400 truncate">{concept.product_headline}</div>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-600 flex-shrink-0">
                        {k1Mean.toFixed(1)}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface IndividualReportsSectionProps {
  concepts: MultiConceptConceptData[];
  activeIndex: number;
  onTabChange: (index: number) => void;
  winnerIndex: number;
  onShowConceptInfo: (index: number) => void;
  diffVariable?: string; // What's being compared (e.g., "Gummy count")
}

const IndividualReportsSection = React.forwardRef<HTMLDivElement, IndividualReportsSectionProps>(({
  concepts,
  activeIndex,
  onTabChange,
  winnerIndex,
  onShowConceptInfo,
}, ref) => {
  const activeConcept = concepts[activeIndex];

  if (!activeConcept) return null;

  const displayName = activeConcept.brand_name || `Concept ${activeIndex + 1}`;

  // Detect duplicate brand names to show color indicators
  const brandNameCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    concepts.forEach(c => {
      const name = c.brand_name || 'Concept';
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [concepts]);

  // Check if any names are duplicated
  const hasDuplicates = Object.values(brandNameCounts).some(count => count > 1);

  return (
    <div ref={ref}>
      <SectionLabel num="04" label="Individual Reports" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          Detailed concept <span className="italic font-normal" style={{ color: BRAND.accent }}>reports.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Dive into the full analysis for each concept tested.
        </p>
      </div>

      {/* Concept navigation: tabs for a handful of concepts, searchable dropdown for many */}
      {concepts.length > MAX_TABS_BEFORE_DROPDOWN ? (
        <ConceptDropdownSelector
          concepts={concepts}
          activeIndex={activeIndex}
          onTabChange={onTabChange}
          winnerIndex={winnerIndex}
          onShowConceptInfo={onShowConceptInfo}
        />
      ) : (
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-1 -mb-px overflow-x-auto">
            {concepts.map((concept, idx) => {
              const brandName = concept.brand_name || `Concept ${idx + 1}`;
              const isWinner = idx === winnerIndex;
              const isActive = activeIndex === idx;
              // Show color indicator if there are duplicate names
              const showColorIndicator = hasDuplicates && brandNameCounts[brandName] > 1;
              const conceptColor = CONCEPT_COLORS[idx % CONCEPT_COLORS.length];
              const diffValue = getDisplayableDiffValue(concept.diff_value);

              return (
                <button
                  key={idx}
                  onClick={() => onTabChange(idx)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 flex-shrink-0 ${
                    isActive
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {showColorIndicator && (
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: conceptColor }}
                      title={`Concept ${idx + 1}`}
                    />
                  )}
                  {isWinner && <Trophy className="w-3.5 h-3.5" style={{ color: BRAND.accent }} />}
                  <div className="flex flex-col items-start">
                    <span className="whitespace-nowrap">{brandName}</span>
                    {/* Show diff_value to differentiate concepts with same name */}
                    {diffValue && (
                      <span className="text-[10px] text-gray-400 font-normal whitespace-nowrap">
                        {diffValue}
                      </span>
                    )}
                  </div>
                  <ConceptInfoButton onClick={() => onShowConceptInfo(idx)} />
                  {isWinner && <span className="text-[10px] text-teal-600 whitespace-nowrap">(Winner)</span>}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {/* Active Report Content - Multi-concept individual view */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <MultiConceptIndividualReport concept={activeConcept} displayName={displayName} />
      </div>
    </div>
  );
});

/* ═══════════════════════════════════════════════════
   INDIVIDUAL REPORT FOR MULTI-CONCEPT
   Converts MultiConceptConceptData to VitaminReport format
   and reuses the same components as single-concept view
   ═══════════════════════════════════════════════════ */

const MultiConceptIndividualReport: React.FC<{ concept: MultiConceptConceptData; displayName: string }> = ({
  concept,
}) => {
  // Convert MultiConceptConceptData to VitaminReport format
  // so we can reuse the same components as single-concept view
  const report: VitaminReport = useMemo(() => {
    const segEntries = Object.entries(concept.segments || {});
    const totalAgents = segEntries.reduce((sum, [, seg]) => sum + (seg.n_agents || 0), 0);

    // Aggregate distribution data from segments into concept-level k_scores
    // In multi-concept mode, concept-level k_scores don't have distribution,
    // but segment-level metrics do - we need to aggregate them
    const kScoresWithDistribution: Record<string, KScore> = {};
    const kKeys = K_ORDER.filter(k => concept.k_scores[k]);

    for (const k of kKeys) {
      const baseScore = concept.k_scores[k];
      if (!baseScore) continue;

      // Aggregate distribution from all segments
      const aggregatedDist: Record<string, number> = {};
      let hasAnyDistribution = false;

      for (const [, seg] of segEntries) {
        const segMetric = seg.metrics?.[k];
        if (segMetric?.distribution) {
          hasAnyDistribution = true;
          for (const [rating, count] of Object.entries(segMetric.distribution)) {
            aggregatedDist[rating] = (aggregatedDist[rating] || 0) + (count as number);
          }
        }
      }

      // Fall back to the concept-level distribution when segments carry none
      // (segment-less multi-concept runs have distributions on k_scores directly)
      kScoresWithDistribution[k] = {
        ...baseScore,
        distribution: hasAnyDistribution ? aggregatedDist : baseScore.distribution,
      };
    }

    return {
      test_id: `multi-concept-${concept.index}`,
      concept: {
        product_headline: concept.product_headline,
        brand_name: concept.brand_name,
        price_point: concept.price_point,
        product_image_url: concept.product_image_url,
      },
      population: '',
      n_agents: concept.n_agents || totalAgents,
      timestamp: new Date().toISOString(),
      overall_composite_pct: Math.round(concept.overall_mean * 20), // Convert 5-point to percentage
      k_scores: kScoresWithDistribution,
      segments: concept.segments || {},
      friction_drivers: concept.friction_drivers,
      generated: concept.generated,
      _config: {
        product_image_url: concept.product_image_url,
      },
    };
  }, [concept]);

  const kKeys = K_ORDER.filter(k => report.k_scores[k]);
  const segEntries = Object.entries(report.segments || {})
    .map(([id, seg]) => ({ id, ...seg }))
    .sort((a, b) => (b.metrics?.k1?.pct ?? b.metrics?.k1?.mean ?? 0) - (a.metrics?.k1?.pct ?? a.metrics?.k1?.mean ?? 0));

  return (
    <div className="space-y-10">
      {/* Use same components as single-concept view */}
      <ExecSummary report={report} kKeys={kKeys} />
      {segEntries.length > 0 && <SegmentBreakdown report={report} kKeys={kKeys} segEntries={segEntries} />}
      <FrictionAndDrivers report={report} />
      {report.generated?.overall_insights?.cards && <OverallInsights report={report} />}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   COVER
   ═══════════════════════════════════════════════════ */

interface CoverSectionProps {
  report: VitaminReport;
  displayName: string;
  conceptString?: string;
  onShowConceptInfo?: () => void;
}

const CoverSection: React.FC<CoverSectionProps> = ({ report, displayName, conceptString, onShowConceptInfo }) => {
  const segCount = Object.keys(report.segments || {}).length;
  const productImageUrl = report.concept?.product_image_url || report._config?.product_image_url;
  const hasImage = productImageUrl && productImageUrl.trim() !== '';

  const stats = [
    { value: `N=${report.n_agents}`, label: 'Consumer twins' },
    { value: '1', label: 'Concepts tested' },
    { value: String(segCount), label: 'Segments' },
    { value: String(Object.keys(report.k_scores || {}).length), label: 'Core metrics' },
    // Add reliability if confidence data is available
    ...(report.norms?.confidence?.overall_reliability !== undefined
      ? [{ value: `${report.norms.confidence.overall_reliability}%`, label: 'Reliability' }]
      : []),
  ];

  return (
    <div className="rounded-lg shadow-sm overflow-hidden relative" style={{ backgroundColor: BRAND.dark }}>
      {/* Decorative arcs - only show if no image */}
      {!hasImage && (
        <>
          <div
            className="absolute pointer-events-none"
            style={{
              width: 600, height: 600, borderRadius: '50%',
              border: `1px solid ${BRAND.accent}12`,
              top: -200, right: -150,
            }}
          />
          <div
            className="absolute pointer-events-none"
            style={{
              width: 400, height: 400, borderRadius: '50%',
              border: `1px solid ${BRAND.accent}08`,
              top: -80, right: -50,
            }}
          />
        </>
      )}

      <div className="px-5 pt-6 pb-5 md:px-8 md:pt-8 md:pb-6 relative z-10">
        {/* Header: badge + meta */}
        <div className="flex flex-col gap-4 mb-8 md:mb-10 md:flex-row md:items-start md:justify-between md:gap-6">
          <div className="flex items-center gap-3">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.09em] px-3 py-1.5 rounded-full"
              style={{ backgroundColor: BRAND.accent, color: BRAND.dark }}
            >
              BluePill AI
            </span>
            {report.concept?.brand_name && (
              <span className="text-xs text-white/40 tracking-wide flex items-center">
                × {report.concept.brand_name}
                {conceptString && onShowConceptInfo && (
                  <ConceptInfoButton
                    onClick={onShowConceptInfo}
                    className="text-white/40 hover:text-[#07E5D1] hover:bg-white/10"
                  />
                )}
              </span>
            )}
          </div>
          <div className="text-[11px] text-white/35 text-right leading-relaxed">
            {report.timestamp && <>Prepared: {new Date(report.timestamp).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}<br /></>}
            {report.test_id && <>Study ID: BP-CT-{report.test_id.slice(0, 6)}<br /></>}
            Version: 1.0 — Final
          </div>
        </div>

        {/* Main content area - flex layout for image support */}
        <div className={`flex ${hasImage ? 'gap-8' : ''}`}>
          {/* Left: Title and content */}
          <div className="flex-1">
            {/* Title block */}
            <div className="mb-12">
              <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-4">CPG Concept Test Report</div>
              <h2 className="text-2xl md:text-3xl font-semibold text-white leading-tight mb-3">
                Concept Performance<br />
                <em className="font-normal text-white/40">& Consumer Response</em>
              </h2>
              <p className="text-sm text-white/50 leading-relaxed max-w-2xl">
                Testing: <strong className="text-white/80">{displayName}</strong>
                {report.concept?.product_headline && (
                  <> — {report.concept.product_headline}</>
                )}
              </p>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap gap-x-8 gap-y-4 md:gap-12 pt-6" style={{ borderTop: `1px solid ${BRAND.accent}18` }}>
              {stats.map(s => (
                <div key={s.label}>
                  <div className="text-4xl font-bold leading-none mb-1 tabular-nums" style={{ color: BRAND.accent }}>{s.value}</div>
                  <div className="text-[10px] font-medium uppercase tracking-wider text-white/35">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Product Image (if available) */}
          {hasImage && (
            <div className="flex-shrink-0 w-48 lg:w-56">
              <div
                className="rounded-lg overflow-hidden border-2 h-full flex items-center justify-center p-2"
                style={{ borderColor: `${BRAND.accent}40`, backgroundColor: 'rgba(255,255,255,0.05)' }}
              >
                <img
                  src={productImageUrl}
                  alt={report.concept?.brand_name || 'Product'}
                  className="max-w-full max-h-48 object-contain rounded"
                  onError={(e) => {
                    // Hide image container if image fails to load
                    (e.target as HTMLImageElement).parentElement!.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer note */}
      <div className="px-8 py-3 flex items-center justify-between gap-4 relative z-10" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-[10px] text-white/25 leading-relaxed">
          Produced using BluePill AI&rsquo;s synthetic consumer twin methodology. All responses are AI-simulated personas calibrated to behavioral, attitudinal, and category-specific shopper data.
        </p>
        <span className="text-[9px] uppercase tracking-wider text-white/20 font-semibold whitespace-nowrap">Confidential</span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   PIE CHART CARD — For metric distribution visualization
   ═══════════════════════════════════════════════════ */

interface PieChartCardProps {
  metricKey: string;
  score: KScore;
  label: string;
}

// Brand colors for pie chart segments
const PIE_COLORS = [BRAND.accent, BRAND.dark, '#6b7280', '#9ca3af', '#d1d5db'];

// Label mappings for each metric (5 = best, 1 = worst)
const SCORE_LABELS: Record<string, Record<string, string>> = {
  k1: { // Purchase Intent
    '5': 'Definitely Would Buy',
    '4': 'Probably Would Buy',
    '3': 'Might or Might Not Buy',
    '2': 'Probably Would Not Buy',
    '1': 'Definitely Would Not Buy',
  },
  k2: { // Value Perception
    '5': 'Excellent Value',
    '4': 'Good Value',
    '3': 'Fair Value',
    '2': 'Poor Value',
    '1': 'Very Poor Value',
  },
  k3: { // New & Different
    '5': 'Very New & Different',
    '4': 'Somewhat New & Different',
    '3': 'Neutral',
    '2': 'Not Very New & Different',
    '1': 'Not At All New & Different',
  },
  k4: { // Believability
    '5': 'Completely Believable',
    '4': 'Somewhat Believable',
    '3': 'Neutral',
    '2': 'Somewhat Unbelievable',
    '1': 'Not At All Believable',
  },
};

const PieChartCard: React.FC<PieChartCardProps> = ({ metricKey, score, label }) => {
  const labelMap = SCORE_LABELS[metricKey] || {};
  const [hoveredSegment, setHoveredSegment] = useState<{ label: string; count: number; pct: string } | null>(null);

  // Ensure all 5 options are shown with minimum values
  const ensureAllOptions = (dist: Record<string, number>): Record<string, number> => {
    const allRatings = ['5', '4', '3', '2', '1'];
    const MIN_VALUE = 2;
    const result: Record<string, number> = { ...dist };

    // Initialize missing options with 0
    allRatings.forEach(rating => {
      if (result[rating] === undefined) {
        result[rating] = 0;
      }
    });

    // Count how many options need minimum values
    const missingOptions = allRatings.filter(r => result[r] === 0);
    const totalNeeded = missingOptions.length * MIN_VALUE;
    const existingTotal = Object.values(result).reduce((sum, v) => sum + v, 0);

    if (totalNeeded > 0 && existingTotal > totalNeeded) {
      // Distribute from largest values proportionally
      const existingOptions = allRatings.filter(r => result[r] > 0);

      existingOptions.forEach(r => {
        const proportion = result[r] / existingTotal;
        const subtract = Math.floor(proportion * totalNeeded);
        result[r] = Math.max(result[r] - subtract, 1);
      });

      // Add minimum values to missing options
      missingOptions.forEach(r => {
        result[r] = MIN_VALUE;
      });
    } else if (existingTotal === 0) {
      // No data - don't create fake data, just show empty
      return dist;
    }

    return result;
  };

  const distribution = ensureAllOptions(score.distribution || {});

  // Show all entries sorted by score descending (5, 4, 3, 2, 1)
  const entries = Object.entries(distribution).sort((a, b) => Number(b[0]) - Number(a[0]));
  const total = entries.reduce((sum, [, count]) => sum + count, 0);

  // Generate pie chart paths with tooltip data
  const generatePiePaths = () => {
    if (total === 0) return [];

    const paths: { d: string; fill: string; label: string; count: number; pct: string }[] = [];
    let currentAngle = -90; // Start from top
    const cx = 60;
    const cy = 60;
    const r = 59;

    entries.forEach(([scoreKey, count], idx) => {
      const angle = (count / total) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      const displayLabel = labelMap[scoreKey] || `Score ${scoreKey}`;
      const pct = ((count / total) * 100).toFixed(0);

      // Handle full circle case
      if (angle >= 360) {
        paths.push({
          d: `M ${cx},${cy} m -${r},0 a ${r},${r} 0 1,1 ${r * 2},0 a ${r},${r} 0 1,1 -${r * 2},0`,
          fill: PIE_COLORS[idx % PIE_COLORS.length],
          label: displayLabel,
          count,
          pct,
        });
      } else {
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;

        const x1 = cx + r * Math.cos(startRad);
        const y1 = cy + r * Math.sin(startRad);
        const x2 = cx + r * Math.cos(endRad);
        const y2 = cy + r * Math.sin(endRad);

        const largeArc = angle > 180 ? 1 : 0;

        paths.push({
          d: `M ${cx},${cy} L ${x1.toFixed(1)},${y1.toFixed(1)} A ${r},${r} 0 ${largeArc},1 ${x2.toFixed(1)},${y2.toFixed(1)} Z`,
          fill: PIE_COLORS[idx % PIE_COLORS.length],
          label: displayLabel,
          count,
          pct,
        });
      }

      currentAngle = endAngle;
    });

    return paths;
  };

  const piePaths = generatePiePaths();

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 flex gap-5">
      {/* Left: Pie chart with hover tooltips */}
      <div className="flex-shrink-0 relative">
        <svg width="120" height="120" viewBox="0 0 120 120" className="cursor-pointer">
          {piePaths.map((path, idx) => (
            <path
              key={idx}
              d={path.d}
              fill={path.fill}
              className="transition-opacity hover:opacity-80"
              onMouseEnter={() => setHoveredSegment({ label: path.label, count: path.count, pct: path.pct })}
              onMouseLeave={() => setHoveredSegment(null)}
            />
          ))}
          {piePaths.length === 0 && (
            <circle cx="60" cy="60" r="59" fill="#e5e7eb" />
          )}
        </svg>
        {/* Custom tooltip */}
        {hoveredSegment && (
          <div
            className="absolute z-50 px-3 py-2 text-xs bg-gray-900 text-white rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
            style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
          >
            <div className="font-medium">{hoveredSegment.label}</div>
            <div className="text-gray-300">{hoveredSegment.count} responses ({hoveredSegment.pct}%)</div>
          </div>
        )}
      </div>

      {/* Right: Metric label + all options legend */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: BRAND.accent }}>
            {label}
          </div>
          <div className="text-xs font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: BRAND.accentLight, color: BRAND.accent }}>
            Top 2 Box: {(() => {
              const dist = score.distribution || {};
              const top2 = (dist['5'] || 0) + (dist['4'] || 0);
              const t = Object.values(dist).reduce((sum, v) => sum + v, 0);
              return t > 0 ? Math.round((top2 / t) * 100) : 0;
            })()}%
          </div>
        </div>

        {/* All options legend with mapped labels */}
        <div className="space-y-1.5">
          {entries.map(([scoreKey, count], idx) => {
            const pct = total > 0 ? ((count / total) * 100).toFixed(0) : '0';
            const displayLabel = labelMap[scoreKey] || `Score ${scoreKey}`;
            return (
              <div key={scoreKey} className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }}
                />
                <div className="flex-1 text-[11px] text-gray-700 leading-tight">{displayLabel}</div>
                <div className="text-[11px] font-medium text-gray-500 tabular-nums">{count} ({pct}%)</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 01 — Executive Summary & Overall Score
   ═══════════════════════════════════════════════════ */

const ExecSummary: React.FC<{ report: VitaminReport; kKeys: string[]; testId?: string }> = ({ report, kKeys, testId }) => {
  const ks = report.k_scores;

  // Get product image URL
  const productImageUrl = report.concept?.product_image_url || report._config?.product_image_url;
  const hasImage = productImageUrl && productImageUrl.trim() !== '';

  // Check for norms (either from API benchmark or hardcoded)
  const k1Norm = getNorm(ks.k1, testId, 'k1');
  const hasNorms = k1Norm !== null;

  // Get delta: use API delta if available, otherwise calculate
  const getDelta = (kScore: KScore | undefined, key: string): number | null => {
    // Use API-provided delta if available
    if (kScore?.delta !== undefined) {
      return kScore.delta;
    }
    // Fall back to manual calculation with hardcoded norm
    const norm = getNorm(kScore, testId, key);
    if (norm === null || kScore?.mean === undefined) return null;
    return kScore.mean - norm;
  };

  // Get verdict: use API verdict if available, otherwise calculate
  const getVerdict = (kScore: KScore | undefined, key: string): 'pass' | 'warn' | 'fail' | null => {
    // Use API-provided verdict if available
    if (kScore?.verdict) {
      return kScore.verdict;
    }
    // Fall back to calculation
    const delta = getDelta(kScore, key);
    if (delta === null) return null;
    return delta >= 0 ? 'pass' : 'fail';
  };

  return (
    <div>
      <SectionLabel num="01" label="Executive Summary & Overall Score" />

      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          Overall concept <span className="italic font-normal" style={{ color: BRAND.accent }}>performance.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Top-line verdict across all segments and metrics before breaking down by individual dimension and audience.
        </p>
      </div>

      {/* Hero row: main score + image + PI + 3 metric cards */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-4 mt-6 mb-6">
        {/* Main score card with image and Purchase Intent */}
        <div className="text-white rounded-lg shadow-sm p-8 flex flex-col justify-between min-h-[280px]" style={{ backgroundColor: BRAND.dark }}>
          <div className="flex justify-between">
            <div className="flex-1">
              {/* Purchase Intent as main metric */}
              {ks.k1 && (() => {
                const k1Delta = getDelta(ks.k1, 'k1');
                const k1Verdict = getVerdict(ks.k1, 'k1');
                return (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-[10px] uppercase tracking-[0.12em] text-white/45">Purchase Intent</div>
                      {hasNorms && k1Verdict && (
                        <span className={`text-[10px] font-medium ${k1Verdict === 'pass' ? 'text-teal-300' : k1Verdict === 'warn' ? 'text-amber-300' : 'text-red-300'}`}>
                          {k1Verdict === 'pass' ? 'Above norm' : k1Verdict === 'warn' ? 'At norm' : 'Below norm'}
                        </span>
                      )}
                    </div>
                    <div className="text-7xl font-bold leading-none mb-4 tabular-nums" style={{ color: BRAND.accent }}>
                      {ks.k1.mean?.toFixed(1) || '—'}
                      <span className="text-2xl font-normal text-white/40"> / 5.0</span>
                    </div>
                    {hasNorms ? (
                      <div className="flex items-center gap-6 text-sm border-t border-white/10 pt-4">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Top 2 Box</div>
                          <div className="text-white/80 font-semibold">{calculateTop2Box(ks.k1)}%</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1 flex items-center gap-1">
                            Norm
                            <span className="relative group">
                              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white/10 hover:bg-white/20 cursor-help transition-colors">
                                <Info className="w-2.5 h-2.5 text-white/50" />
                              </span>
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs bg-gray-900 text-white rounded-lg shadow-lg whitespace-normal w-64 text-left font-normal normal-case tracking-normal opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                                A norm refers to a standard or benchmark that represents typical values, behaviors or responses within a specific population or industry.
                              </span>
                            </span>
                          </div>
                          <div className="text-white/80 font-semibold">{getNorm(ks.k1, testId, 'k1')?.toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Delta</div>
                          <div className={`font-semibold ${k1Delta !== null && k1Delta >= 0 ? 'text-teal-400' : 'text-red-400'}`}>
                            {k1Delta !== null ? (k1Delta >= 0 ? '+' : '') + k1Delta.toFixed(2) : '—'}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-white/60">
                        Top 2 Box: {calculateTop2Box(ks.k1)}%
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            {/* Product Image */}
            {hasImage && (
              <div className="flex-shrink-0 w-32 ml-4 flex items-start">
                <div className="rounded-lg overflow-hidden border-2 p-2" style={{ borderColor: `${BRAND.accent}40`, backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <img
                    src={productImageUrl}
                    alt={report.concept?.brand_name || 'Product'}
                    className="max-w-full max-h-32 object-contain rounded"
                    onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
                  />
                </div>
              </div>
            )}
          </div>

          {report.generated?.verdict_text && (
            <div
              className="text-sm leading-relaxed text-white/70 pt-4 mt-4"
              style={{ borderTop: `1px solid ${BRAND.accent}25` }}
            >
              <RadiusText path="verdict" text={report.generated.verdict_text} tone="dark" />
            </div>
          )}
        </div>

        {/* 3 metric cards (k2, k3, k4 - excluding k1 which is in main card) */}
        <div className="flex flex-col gap-3">
          {kKeys.filter(k => k !== 'k1').map(k => {
            const score = ks[k];
            if (!score) return null;
            const norm = getNorm(score, testId, k);
            const delta = getDelta(score, k);
            const verdict = getVerdict(score, k);
            const hasNormForK = norm !== null;
            return (
              <div key={k} className="rounded-lg border border-gray-200 bg-white p-4 flex-1">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: BRAND.dark }}>
                    {K_LABELS[k] || k}
                  </div>
                  {hasNormForK && verdict && (
                    <span className={`text-[10px] font-medium ${verdict === 'pass' ? 'text-teal-600' : 'text-red-600'}`}>
                      {verdict === 'pass' ? 'Above norm' : 'Below norm'}
                    </span>
                  )}
                </div>
                <div className="text-3xl font-bold leading-none text-gray-900 tabular-nums">
                  {score.mean?.toFixed(1) || '—'}
                  <span className="text-sm font-normal text-gray-400"> / 5.0</span>
                </div>
                {hasNormForK ? (
                  <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                    <span>Top 2: <span className="font-semibold text-gray-700">{calculateTop2Box(score)}%</span></span>
                    <span className="inline-flex items-center gap-1">
                      Norm:
                      <span className="relative group">
                        <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gray-100 hover:bg-gray-200 cursor-help transition-colors">
                          <Info className="w-2.5 h-2.5 text-gray-400" />
                        </span>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs bg-gray-900 text-white rounded-lg shadow-lg whitespace-normal w-64 text-left opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                          A norm refers to a standard or benchmark that represents typical values, behaviors or responses within a specific population or industry.
                        </span>
                      </span>
                      <span className="font-semibold text-gray-700">{norm.toFixed(2)}</span>
                    </span>
                    <span className={`font-semibold ${delta !== null && delta >= 0 ? 'text-teal-600' : 'text-red-600'}`}>
                      {delta !== null ? (delta >= 0 ? '+' : '') + delta.toFixed(2) : '—'}
                    </span>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 mt-1">
                    Top 2 Box: {calculateTop2Box(score)}%
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pie Chart Grid - 2x2 layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {kKeys.map(k => {
          const score = ks[k];
          if (!score) return null;
          return (
            <PieChartCard
              key={k}
              metricKey={k}
              score={score}
              label={K_LABELS[k] || k}
            />
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 02 — Segment Breakdown
   ═══════════════════════════════════════════════════ */

interface SegEntry extends SegmentData { id: string; }

const SEGMENT_SCORE_MAX = 5;

/** Below md the 9-column segment table collapses to one readable card per segment. */
const SegmentCard: React.FC<{ seg: SegEntry; rank: number; kKeys: string[]; insight: string }> = ({ seg, rank, kKeys, insight }) => (
  <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
    <div className="flex items-baseline gap-2">
      <span className="text-xs text-gray-400 font-mono tabular-nums">{rank}</span>
      <h4 className="font-semibold text-gray-900 text-[15px] leading-tight flex-1 min-w-0">{seg.name}</h4>
      <span className="text-[10px] text-gray-400 whitespace-nowrap">{seg.n_agents} agents</span>
    </div>
    {seg.description && <p className="text-xs text-gray-500 leading-snug mt-1.5">{seg.description}</p>}

    <div className="grid grid-cols-2 gap-3 mt-4">
      {kKeys.map(k => {
        const m = seg.metrics?.[k];
        const vs = VERDICT_STYLE[m?.verdict ?? ''] || VERDICT_STYLE.warn;
        return (
          <div key={k}>
            <div className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mb-1">{K_LABELS[k] || k}</div>
            <div className="text-lg font-bold text-gray-900 leading-none mb-1.5 tabular-nums">
              {m?.mean?.toFixed(1) ?? '—'}
              <span className="text-xs font-normal text-gray-400"> / {SEGMENT_SCORE_MAX}</span>
            </div>
            <div className="h-[3px] bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${vs.dotClass}`} style={{ width: `${m?.mean ? (m.mean / SEGMENT_SCORE_MAX) * 100 : 0}%` }} />
            </div>
          </div>
        );
      })}
    </div>

    {(seg.top_strength?.label || seg.primary_friction?.label) && (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 mt-4">
        {seg.top_strength?.label && (
          <>
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Strength</span>
            <span
              className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${BRAND.accent}20`, color: BRAND.dark }}
            >
              {seg.top_strength.label}
            </span>
          </>
        )}
        {seg.primary_friction?.label && (
          <>
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Friction</span>
            <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700">
              {seg.primary_friction.label}
            </span>
          </>
        )}
      </div>
    )}

    {insight && (
      <p className="text-xs text-gray-600 leading-relaxed mt-3 pt-3 border-t border-gray-100">
        <RadiusText path={`segment_insights.${seg.id}`} text={insight} />
      </p>
    )}
  </div>
);

const SegmentBreakdown: React.FC<{ report: VitaminReport; kKeys: string[]; segEntries: SegEntry[] }> = ({ report, kKeys, segEntries }) => {
  const best = segEntries[0];
  const worst = segEntries[segEntries.length - 1];

  // Get best segment's PI score
  const bestPIMean = best?.metrics?.k1?.mean ?? 0;
  const worstPIMean = worst?.metrics?.k1?.mean ?? 0;
  const piGap = Math.abs(bestPIMean - worstPIMean);

  // Find weakest metric using mean scores
  const weakestK = kKeys.reduce((wk, k) => {
    const curr = report.k_scores[k]?.mean ?? 5;
    const prev = report.k_scores[wk]?.mean ?? 5;
    return curr < prev ? k : wk;
  }, kKeys[0]);

  const weakestMean = report.k_scores[weakestK]?.mean;

  return (
    <div>
      <SectionLabel num="02" label="Segment Breakdown" />

      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          How each segment <span className="italic font-normal" style={{ color: BRAND.accent }}>responded.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Segments ranked by purchase intent. &ldquo;Strength&rdquo; shows the highest-scoring metric per segment; &ldquo;primary friction&rdquo; shows where resistance concentrated.
        </p>
      </div>

      {/* Top-line cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 mb-8">
        <div className="rounded-lg shadow-sm p-5" style={{ backgroundColor: BRAND.accentLight, border: `1px solid ${BRAND.accent}30` }}>
          <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: BRAND.dark }}>Highest Purchase Intent Segment</div>
          <div className="text-xl font-bold text-gray-900 leading-tight mb-1">{best?.name || '—'}</div>
          <div className="text-xs" style={{ color: `${BRAND.dark}88` }}>{bestPIMean.toFixed(1)} / 5.0 purchase intent</div>
          <div className="text-xs mt-1" style={{ color: `${BRAND.dark}88` }}>Top 2 Box: {calculateTop2Box(best?.metrics?.k1)}%</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Biggest PI Gap Across Segments</div>
          <div className="text-xl font-bold text-gray-900 leading-tight mb-1">+{piGap.toFixed(1)}</div>
          <div className="text-xs text-gray-400">{best?.name || '—'} vs. {worst?.name || '—'}</div>
          <div className="text-xs text-gray-400 mt-1">Top 2 Box Gap: {Math.abs(calculateTop2Box(best?.metrics?.k1) - calculateTop2Box(worst?.metrics?.k1))}%</div>
        </div>
        <div className="rounded-lg shadow-sm p-5 text-white" style={{ backgroundColor: BRAND.dark }}>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1">Weakest Metric Overall</div>
          <div className="text-xl font-bold leading-tight mb-1" style={{ color: BRAND.accent }}>{K_LABELS[weakestK] || weakestK}</div>
          <div className="text-xs text-white/40">{weakestMean?.toFixed(1) ?? '—'} / 5.0 across segments</div>
          <div className="text-xs text-white/40 mt-1">Top 2 Box: {calculateTop2Box(report.k_scores[weakestK])}%</div>
        </div>
      </div>

      {/* Cards (mobile) */}
      <div className="md:hidden space-y-3">
        {segEntries.map((seg, idx) => (
          <SegmentCard
            key={seg.id}
            seg={seg}
            rank={idx + 1}
            kKeys={kKeys}
            insight={report.generated?.segment_insights?.[seg.id] || ''}
          />
        ))}
      </div>

      {/* Table (md+) */}
      <div className="hidden md:block bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: BRAND.dark }}>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60 w-8">#</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Segment</th>
                {kKeys.map(k => (
                  <th key={k} className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">
                    {(K_LABELS[k] || k).replace('Purchase Intent', 'PI').replace('Value Perception', 'VP').replace('New & Different', 'N&D').replace('Believability', 'Bel.')}
                  </th>
                ))}
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Top Strength</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Primary Friction</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Key Insight</th>
              </tr>
            </thead>
            <tbody>
              {segEntries.map((seg, idx) => {
                const segInsight = report.generated?.segment_insights?.[seg.id] || '';

                return (
                  <tr key={seg.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors align-top">
                    <td className="px-3 py-4 text-xs text-gray-400 font-mono tabular-nums">{idx + 1}</td>
                    <td className="px-3 py-4">
                      <div className="font-semibold text-gray-900 text-[13px]">{seg.name}</div>
                      {seg.description && <p className="text-[11px] text-gray-500 leading-snug mt-1">{seg.description}</p>}
                      <div className="text-[10px] text-gray-400 mt-0.5">{seg.n_agents} agents</div>
                    </td>
                    {kKeys.map(k => {
                      const m = seg.metrics?.[k];
                      if (!m) return <td key={k} className="px-3 py-4 text-sm text-gray-300">—</td>;
                      const vs = (m.verdict ? VERDICT_STYLE[m.verdict] : undefined) || VERDICT_STYLE.warn;
                      return (
                        <td key={k} className="px-3 py-4">
                          <div className="text-lg font-bold text-gray-900 leading-none mb-1 tabular-nums">
                            {m.mean?.toFixed(1) || '—'}
                            <span className="text-xs font-normal text-gray-400"> / 5</span>
                          </div>
                          <div className="w-14 h-[3px] bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${vs.dotClass}`} style={{ width: `${m.mean ? (m.mean / 5) * 100 : 0}%` }} />
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-4">
                      {seg.top_strength?.label && (
                        <span
                          className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${BRAND.accent}20`, color: BRAND.dark }}
                        >
                          {seg.top_strength.label}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-4">
                      {seg.primary_friction?.label && (
                        <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                          {seg.primary_friction.label}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-4">
                      {segInsight && (
                        <p className="text-[11px] text-gray-600 leading-snug">
                          <RadiusText path={`segment_insights.${seg.id}`} text={segInsight} />
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 03 — Friction & Purchase Drivers
   ═══════════════════════════════════════════════════ */

const FrictionAndDrivers: React.FC<{ report: VitaminReport }> = ({ report }) => {
  const fd = report.friction_drivers || { frictions: [], drivers: [] };
  const frictions = fd.frictions || [];
  const drivers = fd.drivers || [];
  const fdd = report.generated?.friction_driver_descriptions || {};

  return (
    <div>
      <SectionLabel num="03" label="Friction & Purchase Drivers" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          What stopped them, <span className="italic font-normal" style={{ color: BRAND.accent }}>and what pulled them in.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Primary friction categories and purchase drivers ranked by frequency of mention across all segments. Percentages reflect share of respondents surfacing each theme.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Friction section */}
        <div>
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.08em] mb-5 pb-3"
            style={{ color: BRAND.dark, borderBottom: `2px solid ${BRAND.accent}30` }}
          >
            Primary Friction Categories
          </div>
          <div className="space-y-1">
            {frictions.map(f => (
              <div key={f.rank} className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0">
                <div className="text-2xl font-bold leading-none min-w-[28px] tabular-nums" style={{ color: `${BRAND.accent}40` }}>
                  {f.rank}
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-gray-900 mb-1">{f.name}</div>
                  {fdd[f.name] && (
                    <p className="text-[11px] text-gray-500 leading-relaxed mb-1.5">
                      <RadiusText path={`frictions.${f.name}`} text={fdd[f.name]} />
                    </p>
                  )}
                  <div className="text-[10px] text-gray-400 italic">Mentioned by {f.pct}% of consumers</div>
                </div>
                <div className="text-xl font-bold text-red-600 leading-none min-w-[48px] text-right tabular-nums">{f.pct}%</div>
              </div>
            ))}
            {frictions.length === 0 && (
              <div className="text-sm text-gray-400 italic py-4">No friction data available</div>
            )}
          </div>
        </div>

        {/* Driver section */}
        <div>
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.08em] mb-5 pb-3"
            style={{ color: BRAND.dark, borderBottom: `2px solid ${BRAND.accent}30` }}
          >
            Primary Purchase Drivers
          </div>
          <div className="space-y-1">
            {drivers.map(d => (
              <div key={d.rank} className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0">
                <div className="text-2xl font-bold leading-none min-w-[28px] tabular-nums" style={{ color: `${BRAND.accent}40` }}>
                  {d.rank}
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-gray-900 mb-1">{d.name}</div>
                  {fdd[d.name] && (
                    <p className="text-[11px] text-gray-500 leading-relaxed mb-1.5">
                      <RadiusText path={`drivers.${d.name}`} text={fdd[d.name]} />
                    </p>
                  )}
                  <div className="h-[4px] bg-gray-100 rounded-full overflow-hidden mt-2">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${Math.min(d.bar_width ?? d.pct, 100)}%`, backgroundColor: BRAND.accent }}
                    />
                  </div>
                </div>
                <div className="text-xl font-bold leading-none min-w-[48px] text-right tabular-nums" style={{ color: BRAND.dark }}>
                  {d.pct}%
                </div>
              </div>
            ))}
            {drivers.length === 0 && (
              <div className="text-sm text-gray-400 italic py-4">No driver data available</div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 04 — Overall Insights
   ═══════════════════════════════════════════════════ */

const OverallInsights: React.FC<{ report: VitaminReport }> = ({ report }) => {
  const oi = report.generated?.overall_insights;
  if (!oi) return null;

  const cards = oi.cards || [];

  return (
    <div>
      <SectionLabel num="04" label="Overall Insights" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          What the data <span className="italic font-normal" style={{ color: BRAND.accent }}>is telling us.</span>
        </h3>
      </div>

      <RadiusLedger />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {cards.map((card, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: BRAND.accent }} />
            <div className="text-4xl font-bold leading-none mb-3 tabular-nums" style={{ color: `${BRAND.accent}40` }}>
              {idx + 1}
            </div>
            <div className="text-[13px] font-semibold text-gray-900 leading-snug mb-2">
              <RadiusText path={`insight.${card.headline}`} text={card.headline} />
            </div>
            <p className="text-[11.5px] text-gray-500 leading-relaxed">
              {/* `after` skips the sentences already placed in the headline — the API
                  scores an insight card as one run of prose, headline first. */}
              <RadiusText path={`insight.${card.headline}`} text={card.body} after={card.headline} />
            </p>
          </div>
        ))}
      </div>

      {(oi.strongest_signal || oi.biggest_risk) && (
        <div className="rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-6" style={{ backgroundColor: BRAND.accentLight, border: `1px solid ${BRAND.accent}35` }}>
          {oi.strongest_signal && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.09em] mb-2" style={{ color: BRAND.dark }}>Strongest Signal</div>
              <p className="text-[13px] text-gray-900 leading-relaxed">
                <RadiusText path="insight.strongest_signal" text={oi.strongest_signal} />
              </p>
            </div>
          )}
          {oi.biggest_risk && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.09em] text-red-600 mb-2">Biggest Risk</div>
              <p className="text-[13px] text-gray-900 leading-relaxed">
                <RadiusText path="insight.biggest_risk" text={oi.biggest_risk} />
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VitaminConceptTestResults;
